import { useState, useEffect } from 'react';

interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  isNative: boolean; // Running inside Capacitor/native wrapper
}

const detectPlatform = (): PlatformInfo => {
  if (typeof window === 'undefined') {
    return { isIOS: false, isAndroid: false, isMobile: false, isNative: false };
  }
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  
  // iPadOS 13+ reports as Mac, so we need additional checks
  const isIPad = /iPad/.test(userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
    (/Macintosh/.test(userAgent) && 'ontouchend' in document);
  
  const isIOS = (/iPhone|iPod/.test(userAgent) || isIPad) && !(window as any).MSStream;
  const isAndroid = /android/i.test(userAgent);
  const isMobile = isIOS || isAndroid || /webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  
  // Check if running in Capacitor native context
  // Capacitor injects window.Capacitor when running in native app
  const capacitor = (window as any).Capacitor;
  const href = window.location?.href || '';
  const protocol = window.location?.protocol || '';

  // In native iOS app, protocol is typically capacitor://localhost (Capacitor may be injected after first render)
  const isNative = !!(
    capacitor?.isNativePlatform?.() ||
    capacitor?.isNative ||
    protocol === 'capacitor:' ||
    protocol === 'ionic:' ||
    href.startsWith('capacitor://') ||
    href.startsWith('ionic://')
  );

  return { isIOS, isAndroid, isMobile, isNative };
};

export const usePlatform = (): PlatformInfo => {
  const [platform, setPlatform] = useState<PlatformInfo>(detectPlatform);

  useEffect(() => {
    // Re-detect a few times in case Capacitor injects window.Capacitor after mount
    const update = () => setPlatform(detectPlatform());

    update();

    const t1 = window.setTimeout(update, 300);
    const t2 = window.setTimeout(update, 1200);
    const t3 = window.setTimeout(update, 2500);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, []);

  return platform;
};

// Static helper for immediate checks (SSR-safe)
export const getPlatformInfo = (): PlatformInfo => detectPlatform();
